// Enums
import ComparisonCategory from '@/types/comparison-category';
import { COMPARISON_CATEGORIES } from './constants';

// Utils
import { flatten, deepClone } from './objects';
import ComparisonField from '@/types/comparison-field';
import Markup from '@/types/markup';
import SelectedCategories from '@/types/selected-categories';
import MarkupShapes from '@/types/markup-shapes';
import ComparisonItem from '@/types/comparison-item';

// Constants
export enum MARKUP_TOOLS {
  HIGHLIGHT = 'HIGHLIGHT',
  COMMENT = 'COMMENT',
  SQUARE = 'SQUARE',
  CIRCLE = 'CIRCLE',
}

export enum DOT_COLORS {
  GREEN = 'green',
}

/**
 * Selects all available fields in a category
 * @param {ComparisonCategory} category category to add with all fields selected
 * @param {ComparisonField[]} availableFields available fields for selection in the category
 * @returns {Object} Category with all available fields selected
 */
export const selectCategoryFields = (category: ComparisonCategory, availableFields: ComparisonField[]) => ({
  [category.key]: {
    fields: availableFields.map((field: ComparisonField) => field.key),
  },
});

/**
 * Check if a field is currently selected or not
 * Note: Checking all selected fields because some fields are duplicated across categories
 * @param {ComparisonField} field selected field
 * @param {SelectedCategories} selectedCategories current selected categories
 * @returns {boolean} returns true if the field is selected
 */
export const fieldIsSelected = (field: ComparisonField, category: ComparisonCategory, selectedCategories: SelectedCategories) => {
  if (category.key === COMPARISON_CATEGORIES.KEY_METRICS) {
    const selectedFields = Object.values(flatten(selectedCategories));

    return selectedFields.some(f => f === field.key);
  }

  return selectedCategories[category.key] && selectedCategories[category.key].fields.some(f => f === field.key);
};

/**
 * Return the available fields for selection in the category
 * Note: Key metrics will have duplicate fields filtered
 * @param {ComparisonCategory} category filtered category in comparison table
 * @param {string[]} duplicateFields duplicate fields to omit
 * @returns {ComparisonField[]} the fields that are available for selection in category
 */
export const getAvailableFields = (category: ComparisonCategory, duplicateFields?: string[]) => {
  let availableFields = category.fields.slice();

  // Filter fields in keyMetrics that are duplicated
  if (category.key === COMPARISON_CATEGORIES.KEY_METRICS && duplicateFields) {
    availableFields = availableFields.filter(f => !duplicateFields.includes(f.key));
  }

  return availableFields;
};

/**
 * Update field on comparison item with highlight
 * @param {string} category category field is in
 * @param {string} key key of field to apply the highlight
 * @param {Markup} markup markup object on the item
 * @returns {Markup} updated markup object with highlighted field
 */
export const highlightField = (categoryKey: string, fieldKey: string, markup: Markup) => {
  let obj = {} as Markup;

  if (markup[categoryKey] && markup[categoryKey][fieldKey]) {
    obj = {
      ...markup,
      [categoryKey]: {
        ...markup[categoryKey],
        [fieldKey]: {
          ...markup[categoryKey][fieldKey],
          highlight: true,
        },
      },
    };
  } else {
    obj = {
      ...markup,
      [categoryKey]: {
        ...markup[categoryKey],
        [fieldKey]: {
          highlight: true,
        },
      },
    };
  }

  return { ...markup, ...obj };
};

/**
 * Update field on comparison item with shape outline
 * @param {string} category category field is in
 * @param {string} key key of field to apply the outline
 * @param {Markup} markup markup object on the item
 * @param {string} shape shape to outline cell with
 * @returns {Markup} updated markup object with outline applied to field
 */
export const outlineField = (categoryKey: string, fieldKey: string, markup: Markup, shape: MarkupShapes) => {
  let obj = {} as Markup;

  if (markup[categoryKey] && markup[categoryKey][fieldKey]) {
    obj = {
      ...markup,
      [categoryKey]: {
        ...markup[categoryKey],
        [fieldKey]: {
          ...markup[categoryKey][fieldKey],
          shape,
        },
      },
    };
  } else {
    obj = {
      ...markup,
      [categoryKey]: {
        ...markup[categoryKey],
        [fieldKey]: {
          shape,
        },
      },
    };
  }

  return { ...markup, ...obj };
};

/**
 * Update field on comparison item with comment
 * @param {string} category category field is in
 * @param {string} key key of field to apply the highlight
 * @param {Markup} markup markup object on the item
 * @param {string} value comment value
 * @param {Number} dotNumber dot number
 * @param {Date} timestamp date the comment was posted
 * @param {Boolean} isPosted 'true' if the comment is posted, otherwise 'false'
 * @returns {Markup} markup object with updated comment object
 */
export const updateMarkupComment = (
  categoryKey: string,
  fieldKey: string,
  markup: Markup,
  value: string,
  dotNumber?: number,
  timestamp?: Date,
  isPosted?: boolean,
) => {
  let obj = {} as Markup;

  if (markup[categoryKey] && markup[categoryKey][fieldKey] && markup[categoryKey][fieldKey].comment) {
    obj = {
      ...markup,
      [categoryKey]: {
        ...markup[categoryKey],
        [fieldKey]: {
          ...markup[categoryKey][fieldKey],
          comment: {
            ...markup[categoryKey][fieldKey].comment,
            value,
            dotNumber,
            timestamp,
            isPosted,
          },
        },
      },
    };
  } else if (markup[categoryKey] && markup[categoryKey][fieldKey]) {
    obj = {
      ...markup,
      [categoryKey]: {
        ...markup[categoryKey],
        [fieldKey]: {
          ...markup[categoryKey][fieldKey],
          comment: {
            value,
            dotNumber,
            timestamp,
            isPosted,
          },
        },
      },
    };
  } else {
    obj = {
      ...markup,
      [categoryKey]: {
        ...markup[categoryKey],
        [fieldKey]: {
          comment: {
            value,
            dotNumber,
            timestamp,
            isPosted,
          },
        },
      },
    };
  }

  return { ...markup, ...obj };
};

/**
 * Returns the next dot number available when commenting.
 * @param {ComparisonItem[]} items comparison items
 * @returns {Number} new dot number
 */
export const calcNewDotNumber = (items: ComparisonItem[]) => {
  let max = 0;

  const arrayOfMarkups = items.filter(i => i.markup).map(i => i.markup) as Markup[];

  for (const markup of arrayOfMarkups) {
    for (const category of Object.values(markup)) {
      for (const field of Object.values(category)) {
        if (field.comment && field.comment.dotNumber) {
          max = Math.max(max, field.comment.dotNumber);
        }
      }
    }
  }

  return max + 1;
};

/**
 * Deletes any markup comments on comparison item that were unposted.
 * @param {ComparisonItem} item comparison item
 * @returns {item} updated comparison item with unposted comments removed.
 */
export const deleteUnpostedComments = (item: ComparisonItem) => {
  const copy = deepClone(item);

  if (!copy.markup) {
    return copy;
  }

  for (const category of Object.values(copy.markup)) {
    for (const field of Object.values(category)) {
      if (field.comment && !field.comment.isPosted) {
        delete field.comment;
      }
    }
  }

  return copy;
};

/**
 * Creates a unique id for a field on a comparison item.
 * @param {ComparisonItem} item comparison item
 * @param {ComparisonCategory} category comparison item
 * @param {ComparisonField} item comparison item
 * @returns {string} unique id for the field on a comparison item
 */
export const getUniqueFieldId = (item: ComparisonItem, category: ComparisonCategory, field: ComparisonField) =>
  // Replace brackets, quotes, and whitespace since we can't use them in query selectors
  `${item._id}-${category.key}-${field.key}`.replace(/\[|\]|"|\s/g, '');

/**
 * Checks if a comparison item has markup comments.
 * @param {ComparisonItem} item comparison item
 * @param {SelectedCategories} selectedCategories selected categories on comparison
 * @returns {boolean} `true` if item has comments `false` otherwise
 */
export const itemHasComments = (item: ComparisonItem, selectedCategories?: SelectedCategories) => {
  if (!item.markup || !selectedCategories) {
    return false;
  }

  const selectedFields = Object.values(flatten(selectedCategories));

  for (const category of Object.keys(item.markup)) {
    for (const field of Object.keys(item.markup[category])) {
      if (item.markup[category][field].comment && selectedFields.includes(field)) {
        return true;
      }
    }
  }

  return false;
};
